/**
 * product-extras.js — JS for the landing-slice sections ported into the
 * product template (sections/product-coffee-vs-matcha.liquid).
 *
 * Coffee vs Matcha graph reveal — port of the progress animation in
 * features/landing/Benefits.tsx (ENABLE_GRAPH_ANIMATION = true branch):
 * once 30% of the SVG is visible, the clip rect width animates 0 → full
 * over 1600ms (linear rAF, like the source) and the duration-hint labels
 * fade in with the same progress. Runs once, then disconnects.
 *
 * Markup contract:
 *   <svg data-benefits-graph>
 *     … <rect data-graph-reveal data-full-width="344" width="344" …>
 *     … <g data-graph-hints opacity="1">…</g>
 *   </svg>
 *
 * The markup ships fully drawn (progress = 1) so the graph stays visible
 * without JS; this script re-arms it to the source's initial state
 * (progress = 0) before animating.
 */
(function () {
  "use strict";

  function initGraph(svg) {
    if (svg.__graphInit) return;
    svg.__graphInit = true;

    const rect = svg.querySelector("[data-graph-reveal]");
    if (!rect) return;
    const hints = svg.querySelector("[data-graph-hints]");
    const fullWidth =
      parseFloat(rect.getAttribute("data-full-width") || "") || 344;

    // No IntersectionObserver -> leave the graph fully drawn.
    if (!("IntersectionObserver" in window)) return;

    // Arm: hide like the source's initial state (progress = 0).
    rect.setAttribute("width", "0");
    if (hints) hints.setAttribute("opacity", "0");

    let hasAnimated = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimated) return;
          hasAnimated = true;

          // Start animation (same 1600ms linear ramp as the source).
          const start = performance.now();
          const dur = 1600;
          const tick = (t) => {
            const p = Math.min((t - start) / dur, 1);
            rect.setAttribute("width", String(fullWidth * p));
            if (hints) hints.setAttribute("opacity", String(p));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);

          // Stop observing once animation starts.
          observer.disconnect();
        });
      },
      { threshold: 0.3, rootMargin: "0px" },
    );

    observer.observe(svg);
  }

  function scan(root) {
    (root || document)
      .querySelectorAll("[data-benefits-graph]")
      .forEach(initGraph);
  }

  window.themeUtils.onReady(() => scan());
  document.addEventListener("product-extras:scan", (e) => scan(e.target));
  window.themeProductExtras = { scan: scan };
})();
